import React, { useState, useRef, useEffect, ReactNode } from 'react';
import { motion } from 'framer-motion';
import { MapPin, ChevronDown, Home } from 'lucide-react';
import useLocalCart from '@/hooks/useLocalCart';
import useUser from '@/hooks/useUser';
import { Recipient } from '@/context/types';
import { formatUSAddress } from '@/helpers/misc';
import { CurveDivider } from './PageHeader';
import baker from '@/res/imgs/baker.webp';

export default function OrderManagementToolbar({
  title,
  altText,
}: {
  title: string | ReactNode;
  altText?: string | ReactNode;
}) {
  const { cart, openDatePicker, openLocationPopover } = useLocalCart();
  const { user } = useUser();

  const orderType = 'shipping'; // Can be wired up to state later
  const [isTypeOpen, setIsTypeOpen] = useState(false);

  const handleOpenLocationPopover = (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    const rect = e.currentTarget.getBoundingClientRect();
    openLocationPopover(activeAddress, rect);
  };

  // 1. Address Resolution
  const activeAddress: Recipient =
    cart.recipient && Object.keys(cart.recipient).length > 0
      ? (cart.recipient as Recipient)
      : ({ ...user?.defaultAddress, fullName: user?.fullName } as Recipient);

  const hasValidAddress = Boolean(activeAddress?.street);
  const isOrderReady = hasValidAddress && cart?.lines?.length > 0;

  // Click outside ref for the Type dropdown
  const typeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (typeRef.current && !typeRef.current.contains(event.target as Node)) {
        setIsTypeOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Framer Motion Variants
  const containerVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { staggerChildren: 0.1, duration: 0.4, ease: 'easeOut' },
    },
  } as const;

  const itemVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    // FIX: Removed duplicate x-padding on the outer wrapper that was causing layout squishing
    <div className="relative bg-brand-gold flex flex-col pb-[80px] md:pb-[100px] overflow-hidden">
      {/* 1. Action Toolbar */}
      {/* FIX: Swapped flex-row for flex-wrap so elements can drop down naturally on small mobile screens */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="flex flex-wrap items-center justify-start gap-3 md:gap-4 text-sm md:text-[15px] font-bold text-text-main min-h-[auto] md:min-h-[80px] py-4 px-5 md:px-10 z-20"
      >
        {/* Order Type Selector */}
        <motion.div variants={itemVariants} className="relative" ref={typeRef}>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.96 }}
            onClick={() => setIsTypeOpen(!isTypeOpen)}
            className="flex items-center gap-2 transition-shadow bg-brand-cream hover:bg-white shadow-sm ring-1 ring-black/5 px-3 md:px-4 py-2 rounded-xl cursor-pointer text-text-main"
          >
            <Home size={18} strokeWidth={2.5} />
            <span className="block translate-y-[1px] capitalize font-display">
              {orderType === 'shipping' ? 'Delivery' : 'Pickup'}
            </span>
          </motion.button>
        </motion.div>

        {/* Dynamic Context Action (Address Picker) */}
        <motion.button
          variants={itemVariants}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.96 }}
          onClick={handleOpenLocationPopover}
          className="flex items-center gap-2 transition-shadow bg-brand-cream hover:bg-white shadow-sm ring-1 ring-black/5 px-3 md:px-4 py-2 rounded-xl cursor-pointer"
        >
          <MapPin size={18} strokeWidth={2.5} className="flex-shrink-0" />
          {/* FIX: Improved truncation widths to ensure text cuts off cleanly instead of blowing out the container */}
          <span className="font-medium text-text-main max-w-[140px] sm:max-w-[180px] md:max-w-[250px] truncate block translate-y-[1px]">
            {hasValidAddress
              ? formatUSAddress(activeAddress)
              : 'Add Delivery Address'}
          </span>
          <ChevronDown
            size={16}
            strokeWidth={2.5}
            className="flex-shrink-0 opacity-70"
          />
        </motion.button>

        {/* Status Badge */}
        <motion.span
          variants={itemVariants}
          className={`
            ml-auto md:ml-0 flex items-center px-3 py-1.5 rounded-lg text-[10px] md:text-xs font-bold tracking-widest uppercase shadow-sm font-display
            ${isOrderReady ? 'bg-brand-sage text-white' : 'bg-red-500/90 text-white'}
          `}
        >
          <span className="block translate-y-[1px]">
            {isOrderReady ? 'Order Ready' : 'In Progress'}
          </span>
        </motion.span>
      </motion.div>

      {/* 2. Hero Section (Title & Image) */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
        // FIX: Ensure text has room to breathe, preventing the image from squeezing the title on mobile
        className="flex flex-row justify-between items-center px-5 md:px-10 z-10 w-full"
      >
        <div className="flex-1 flex flex-col gap-2 md:gap-4 py-6 md:py-10 pr-4">
          <h1 className="text-2xl sm:text-3xl md:text-4xl text-brand-truffle font-extrabold tracking-tight leading-tight">
            {title}
          </h1>
          <span className="font-display font-medium text-base md:text-[1.25em] text-brand-truffle/80">
            {altText}
          </span>
        </div>

        {/* Baker Image Wrapper */}
        <motion.div
          whileHover={{ scale: 1.05, rotate: -2 }}
          transition={{ type: 'spring', stiffness: 300 }}
          className="w-[120px] sm:w-[160px] md:w-[250px] flex-shrink-0 flex items-end justify-center relative"
        >
          <img
            src={baker}
            alt={'Add Goodies to Cart!'}
            className="w-full h-auto object-contain drop-shadow-xl"
            style={{
              // This pushes the image right onto the curve for a nice layered design aesthetic
              transform: 'translateY(15px)',
            }}
          />
        </motion.div>
      </motion.div>

      <CurveDivider className="!text-brand-frost absolute bottom-0 left-0 w-full z-0" />
    </div>
  );
}
